import { useState, useEffect } from 'react';
import { User, LogOut, Shield, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/useAuth';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useIsMaster } from '@/hooks/useIsMaster';
import { useNavigate } from 'react-router-dom';
import { ImpersonationSelector } from './ImpersonationSelector';
import { supabase } from '@/integrations/supabase/client';

interface UserMenuProps {
  onNavigate?: (path: string) => void;
  onLogout?: () => void;
}

interface ProfileData {
  nickname: string | null;
  full_name: string | null;
  avatar_url: string | null;
}

export const UserMenu = ({ onNavigate, onLogout }: UserMenuProps) => {
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminAuth();
  const { isMaster } = useIsMaster();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ProfileData | null>(null);

  // Buscar dados do perfil
  useEffect(() => {
    if (!user) return;

    const fetchProfile = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('nickname, full_name, avatar_url')
        .eq('id', user.id)
        .maybeSingle();
      
      if (data) {
        setProfile(data);
      }
    };

    fetchProfile();

    // Escutar mudanças no perfil
    const channel = supabase
      .channel('profile-changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${user.id}`
      }, (payload) => {
        setProfile(payload.new as ProfileData);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleNavigate = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const handleLogout = async () => {
    await signOut();
    if (onLogout) {
      onLogout();
    } else {
      navigate('/');
    }
  };

  if (!user) return null;

  // Prioridade: nickname > primeiro nome do full_name > email
  const displayName = profile?.nickname 
    || profile?.full_name?.split(' ')[0] 
    || user.user_metadata?.full_name?.split(' ')[0]
    || user.email?.split('@')[0] 
    || 'Usuário';
  
  const userInitial = displayName.charAt(0).toUpperCase();
  const avatarUrl = profile?.avatar_url;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Avatar className="h-7 w-7">
            {avatarUrl && (
              <AvatarImage 
                src={avatarUrl} 
                alt={displayName}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {userInitial}
            </AvatarFallback>
          </Avatar>
          <span className="hidden lg:inline text-sm font-medium max-w-[100px] truncate">
            {displayName}
          </span>
          {isMaster && (
            <span className="text-xs text-amber-500">🛠️</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{displayName}</p>
          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          {isMaster && (
            <p className="text-xs text-amber-500 mt-1">Superadmin</p>
          )}
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Seletor de Impersonation - apenas para Master */}
        {isMaster && (
          <>
            <ImpersonationSelector />
            <DropdownMenuSeparator />
          </>
        )}
        
        {/* Link para Admin */}
        {isAdmin && (
          <DropdownMenuItem onClick={() => handleNavigate('/admin')} className="cursor-pointer">
            <Shield className="mr-2 h-4 w-4" />
            Administração
          </DropdownMenuItem>
        )}
        
        {/* Link para Minha Conta - apenas para usuários comuns (não admin/master) */}
        {!isAdmin && !isMaster && (
          <DropdownMenuItem onClick={() => handleNavigate('/painel')} className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            Minha Conta
          </DropdownMenuItem>
        )}
        
        {/* Link para Informações do Negócio */}
        <DropdownMenuItem onClick={() => handleNavigate('/informacoes-negocio')} className="cursor-pointer">
          <Settings className="mr-2 h-4 w-4" />
          Informações do Negócio
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Logout */}
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
